/**
 * Safe HTML for gallery category pages.
 * The words on a category page come from that category's saved fields.
 * This file stays CommonJS and only depends on the language runtime.
 */

const SUMMER_SPECIAL = {
  slug: 'family-summer-special',
  title: 'Family Summer Special | Noordhoek, Cape Town',
  description: 'Family summer special in Cape Town: a 45-minute golden-hour photoshoot near Noordhoek for R2,500, with an edited online gallery, until 15 December 2026.',
  categoryPath: '/galleries/family',
  categoryLinkLabel: 'Family photography in Cape Town',
};

const HOME_PAGE = {
  title: 'Carmen Lorraine Photography | Cape Town Photographer',
  description: 'Creative, natural photography in Cape Town by Carmen Lorraine. Specialising in portraits, lifestyle, commercial, business and property photography.',
  h1: 'Carmen Lorraine Photography',
  intro: 'I’m a full-time professional photographer living and working in beautiful Cape Town, with 16 years behind the lens. I shoot Brand, Business & Lifestyle as well as Property, Travel & Environmental Photography. 📸\n\nTelling stories of people, places and nature is what I love most.  I’m grateful for life on this incredible planet, and I cherish every day. The sights, sounds, and colours of the earth, the sky above, and the galaxies beyond are what inspire my creativity.',
};

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function filled(value) {
  return Boolean(String(value == null ? '' : value).trim());
}

function truncateDescription(value) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= 155) return text;
  const cut = text.slice(0, 155);
  const space = cut.lastIndexOf(' ');
  return (space > 40 ? cut.slice(0, space) : cut).replace(/[\s,;:.-]+$/, '');
}

function pickText(stored, fallback, last) {
  if (filled(stored)) return String(stored).trim();
  if (filled(fallback)) return String(fallback).trim();
  return last;
}

function faqItems(faqs) {
  return (Array.isArray(faqs) ? faqs : [])
    .map((item) => ({
      question: String((item && item.question) || '').replace(/\s+/g, ' ').trim(),
      answer: String((item && item.answer) || '').replace(/\s+/g, ' ').trim(),
    }))
    .filter((item) => item.question && item.answer);
}

function categoryPageDefaults(slug) {
  void slug;
  return {
    seoTitle: '',
    seoDescription: '',
    h1: '',
    intro: '',
    body: '',
    faqs: [],
  };
}

function resolveCategoryFields(category) {
  const source = category && typeof category === 'object' ? category : {};
  return {
    title: filled(source.seoTitle) ? String(source.seoTitle).trim() : '',
    description: filled(source.seoDescription) ? truncateDescription(source.seoDescription) : '',
    h1: filled(source.h1) ? String(source.h1).trim() : '',
    intro: filled(source.intro) ? String(source.intro).trim() : '',
    body: filled(source.body) ? String(source.body).trim() : '',
    faqs: faqItems(source.faqs),
  };
}

function safeHref(url) {
  const raw = String(url || '').trim();
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  if (raw === 'https://carmenlorraine.com' || raw.startsWith('https://carmenlorraine.com/')) return raw;
  if (/^tel:\+?[0-9]+$/.test(raw)) return raw;
  return '';
}

function inline(text) {
  const source = String(text || '');
  const pattern = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g;
  return source.split(pattern).map((part) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return `<strong>${escapeHtml(bold[1])}</strong>`;
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const href = safeHref(link[2]);
      const label = escapeHtml(link[1]);
      if (!href) return label;
      return `<a href="${escapeHtml(href)}">${label}</a>`;
    }
    return escapeHtml(part);
  }).join('');
}

function renderMarkdown(source) {
  const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
  const html = [];
  let list = [];
  const flush = () => {
    if (!list.length) return;
    html.push(`<ul>${list.map((item) => `<li>${inline(item)}</li>`).join('')}</ul>`);
    list = [];
  };
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flush();
      return;
    }
    if (trimmed.startsWith('### ')) {
      flush();
      html.push(`<h3>${inline(trimmed.slice(4))}</h3>`);
      return;
    }
    if (trimmed.startsWith('## ')) {
      flush();
      html.push(`<h2>${inline(trimmed.slice(3))}</h2>`);
      return;
    }
    if (trimmed.startsWith('- ')) {
      list.push(trimmed.slice(2));
      return;
    }
    flush();
    html.push(`<p>${inline(trimmed)}</p>`);
  });
  flush();
  return html.join('');
}

function renderIntroHtml(intro) {
  return String(intro || '')
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${inline(paragraph)}</p>`)
    .join('');
}

function resolveHomePage(stored) {
  const page = stored && typeof stored === 'object' ? stored : {};
  return {
    title: pickText(page.title, HOME_PAGE.title, HOME_PAGE.title),
    description: pickText(page.description, HOME_PAGE.description, HOME_PAGE.description),
    h1: pickText(page.h1, HOME_PAGE.h1, HOME_PAGE.h1),
    intro: pickText(page.intro, HOME_PAGE.intro, HOME_PAGE.intro),
  };
}

function renderHomeIntroHtml(intro) {
  return renderIntroHtml(intro == null ? HOME_PAGE.intro : intro);
}

function renderBodyHtml(body) {
  return renderMarkdown(body);
}

function renderFaqHtml(faqs) {
  const items = faqItems(faqs);
  if (!items.length) return '';
  return items.map((item) => (
    `<h3>${escapeHtml(item.question)}</h3><p>${escapeHtml(item.answer)}</p>`
  )).join('');
}

function renderCategoryContentHtml(filter) {
  const page = filter || {};
  return `${renderIntroHtml(page.intro)}${renderBodyHtml(page.body)}${renderFaqHtml(page.faqs)}`;
}

function renderBodyAndFaqHtml(filter) {
  const page = filter || {};
  return `${renderBodyHtml(page.body)}${renderFaqHtml(page.faqs)}`;
}

function faqPageNode(faqs) {
  const items = faqItems(faqs);
  if (!items.length) return null;
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

function faqPageJson(faqs) {
  const node = faqPageNode(faqs);
  if (!node) return '';
  return JSON.stringify(Object.assign({ '@context': 'https://schema.org' }, node));
}

module.exports = {
  SUMMER_SPECIAL,
  HOME_PAGE,
  escapeHtml,
  truncateDescription,
  resolveCategoryFields,
  categoryPageDefaults,
  resolveHomePage,
  renderIntroHtml,
  renderHomeIntroHtml,
  renderBodyHtml,
  renderFaqHtml,
  renderCategoryContentHtml,
  renderBodyAndFaqHtml,
  faqPageNode,
  faqPageJson,
  safeHref,
};
